import { Eye, X } from "@tamagui/lucide-icons";
import {
  Customer,
  Sensor,
  SensorAlert,
  SensorAlertSeverity,
  SensorAlertType,
  useAlertStatusCounts,
  useCustomers,
  useSensors,
} from "common";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  Button,
  Heading,
  Label,
  Separator,
  Spinner,
  Text,
  XStack,
  YStack,
} from "tamagui";
import { GradientButton, OutlinedButton, Table } from "tamagui-config";

import { GradientCard } from "../../components/GradientCard";
import { Select } from "../../components/Select";
import { StatusPill } from "../../components/StatusPill";
import { Tabs } from "../../components/Tabs";
import { useStatusPillProps } from "../../hooks/useStatusPillProps";

enum Tab {
  Active = "active",
  Cleared = "cleared",
}

export const Alerts = () => {
  const { customers, loading: isCustomersLoading } = useCustomers() || {};
  const { sensors, fetchAllSensors, loading: isSensorsLoading } = useSensors();
  const [selectedTab, setSelectedTab] = useState<Tab>(Tab.Active);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onTabChange = (value: Tab) => {
    setSelectedTab(value);
  };

  const customerFilterOptions = useMemo(
    () => [
      { label: t("common.all"), value: "" },
      ...(customers || [])
        .filter((customer) => !!customer.name)
        .map((customer) => ({
          label: customer.name as string,
          value: customer.id,
        })),
    ],
    [customers],
  );
  const typeFilterOptions = [
    { label: t("common.all"), value: "" },
    ...Object.entries(SensorAlertType).map(([key, value]) => ({
      label: t([
        `alerts.type.${key[0].toLowerCase()}${key.slice(1)}`,
        "alerts.type.unknown",
      ]),
      value,
    })),
  ];
  const severityFilterOptions = [
    { label: t("common.all"), value: "" },
    ...Object.entries(SensorAlertSeverity).map(([key, value]) => ({
      label: t([
        `alerts.severity.${key[0].toLowerCase()}${key.slice(1)}`,
        "alerts.severity.unknown",
      ]),
      value,
    })),
  ];

  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState(
    customerFilterOptions[0].value,
  );
  const [selectedTypeFilter, setSelectedTypeFilter] = useState(
    typeFilterOptions[0].value,
  );
  const [selectedSeverityFilter, setSelectedSeverityFilter] = useState(
    severityFilterOptions[0].value,
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [pageTokens, setPageTokens] = useState<(string | undefined)[]>([
    undefined,
  ]);
  const [hasMorePages, setHasMorePages] = useState(false);

  const [customerFilter, setCustomerFilter] = useState(
    customerFilterOptions[0].value,
  );
  const [typeFilter, setTypeFilter] = useState(typeFilterOptions[0].value);
  const [severityFilter, setSeverityFilter] = useState(
    severityFilterOptions[0].value,
  );

  const { counts: alertStatusCounts } = useAlertStatusCounts({
    customerId: customerFilter || undefined,
    alertType: typeFilter || undefined,
    severity: severityFilter || undefined,
  });

  const [displayedAlerts, setDisplayedAlerts] = useState<SensorAlert[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPagedAlerts = async (pageToken?: string) => {
    setIsLoading(true);
    try {
      const { API, graphql } = await import("common");

      const filters = {
        customerId: customerFilter || undefined,
        alertType: typeFilter || undefined,
        severity: severityFilter || undefined,
        isAcknowledged:
          selectedTab === Tab.Active
            ? false
            : selectedTab === Tab.Cleared
              ? true
              : undefined,
      };

      const response = await API.graphql({
        query: graphql.listSensorAlerts,
        variables: {
          ...filters,
          limit: pageSize,
          nextToken: pageToken,
        },
      });

      const responseData = (response as any).data?.listSensorAlerts;
      const alerts = responseData?.items || [];
      const newNextToken = responseData?.nextToken;

      const sortedAlerts = alerts.sort((a: SensorAlert, b: SensorAlert) => {
        const aTime = a.timestamp || 0;
        const bTime = b.timestamp || 0;
        return bTime - aTime;
      });

      setDisplayedAlerts(sortedAlerts);
      setNextToken(newNextToken);
      setHasMorePages(!!newNextToken);
    } catch (error) {
      console.error("Error fetching paged alerts:", error);
      setDisplayedAlerts([]);
      setHasMorePages(false);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    setCustomerFilter(selectedCustomerFilter);
    setTypeFilter(selectedTypeFilter);
    setSeverityFilter(selectedSeverityFilter);
  };

  const clearFilters = () => {
    setCustomerFilter(customerFilterOptions[0].value);
    setTypeFilter(typeFilterOptions[0].value);
    setSeverityFilter(severityFilterOptions[0].value);

    setSelectedCustomerFilter(customerFilterOptions[0].value);
    setSelectedTypeFilter(typeFilterOptions[0].value);
    setSelectedSeverityFilter(severityFilterOptions[0].value);
  };

  const onViewAlertPress = (sensorAlertId: string) => {
    navigate(`/alerts/${sensorAlertId}`);
  };

  const goToNextPage = () => {
    if (hasMorePages && nextToken) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);

      const newPageTokens = [...pageTokens];
      if (newPageTokens.length === newPage) {
        newPageTokens.push(nextToken);
      }
      setPageTokens(newPageTokens);

      fetchPagedAlerts(nextToken);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);

      const previousToken = pageTokens[newPage - 1];
      fetchPagedAlerts(previousToken);
    }
  };

  const resetPagination = () => {
    setCurrentPage(1);
    setNextToken(undefined);
    setPageTokens([undefined]);
    setHasMorePages(false);
  };

  useEffect(() => {
    resetPagination();
    fetchPagedAlerts();
    fetchAllSensors();
  }, [customerFilter, typeFilter, severityFilter, selectedTab]);

  if (isCustomersLoading || isSensorsLoading || isLoading) {
    return (
      <YStack flex={1} gap="$2" justifyContent="center" alignItems="center">
        <Spinner size="large" />
        <Text>{t("common.loading")}</Text>
      </YStack>
    );
  }

  return (
    <YStack gap="$5">
      <Heading fontSize="$8">{t("alerts.title")}</Heading>

      <Tabs value={selectedTab} onChange={onTabChange}>
        <Tabs.Button
          value={Tab.Active}
          count={alertStatusCounts?.activeCount || 0}
        >
          {t("alerts.tabs.active")}
        </Tabs.Button>
        <Tabs.Button
          value={Tab.Cleared}
          count={alertStatusCounts?.clearedCount || 0}
        >
          {t("alerts.tabs.cleared")}
        </Tabs.Button>
      </Tabs>

      <GradientCard backgroundColor="$transparentBorder" backgroundImage="none">
        <XStack gap="$3" alignItems="center" justifyContent="space-between">
          <XStack gap="$2.5" alignItems="center">
            <Text fontWeight="bold" color="$white12">
              {t("dashboard.filters.heading")}
            </Text>
          </XStack>
          <Button
            variant="outlined"
            height={36}
            paddingVertical={0}
            paddingHorizontal="$3"
            onPress={clearFilters}
          >
            <X size={20} />
            {t("alerts.filters.clear")}
          </Button>
        </XStack>
        <XStack gap="$3.5">
          <YStack gap="$2" flexGrow={1} flexBasis={0}>
            <Label
              htmlFor="customer-filter"
              fontSize="$3"
              lineHeight="$2"
              color="$white10"
            >
              {t("alerts.filters.customer")}
            </Label>
            <Select
              id="customer-filter"
              options={customerFilterOptions}
              value={selectedCustomerFilter}
              onValueChange={setSelectedCustomerFilter}
            />
          </YStack>
          <YStack gap="$2" flexGrow={1} flexBasis={0}>
            <Label
              htmlFor="type-filter"
              fontSize="$3"
              lineHeight="$2"
              color="$white10"
            >
              {t("alerts.filters.type")}
            </Label>
            <Select
              id="type-filter"
              options={typeFilterOptions}
              value={selectedTypeFilter}
              onValueChange={setSelectedTypeFilter}
            />
          </YStack>
          <YStack gap="$2" flexGrow={1} flexBasis={0}>
            <Label
              htmlFor="severity-filter"
              fontSize="$3"
              lineHeight="$2"
              color="$white10"
            >
              {t("alerts.filters.severity")}
            </Label>
            <Select
              id="severity-filter"
              options={severityFilterOptions}
              value={selectedSeverityFilter}
              onValueChange={setSelectedSeverityFilter}
            />
          </YStack>
          <GradientButton
            elevated
            rounded
            alignSelf="flex-end"
            onPress={applyFilters}
          >
            {t("dashboard.filters.apply")}
          </GradientButton>
        </XStack>
      </GradientCard>

      <Separator />

      <AlertsTable
        alerts={displayedAlerts}
        sensors={sensors}
        customers={customers}
        onViewAlertPress={onViewAlertPress}
      />
      <XStack
        justifyContent="space-between"
        alignItems="center"
        marginTop="$6"
        paddingHorizontal="$4"
      >
        <Text fontSize="$3" color="$gray10">
          Page {currentPage} {hasMorePages ? "(more available)" : "(last page)"}
        </Text>
        <XStack gap="$2">
          <OutlinedButton
            size="$3"
            onPress={goToPreviousPage}
            disabled={currentPage === 1}
          >
            Previous
          </OutlinedButton>
          <GradientButton
            size="$3"
            onPress={goToNextPage}
            disabled={!hasMorePages}
          >
            Next
          </GradientButton>
        </XStack>
      </XStack>
    </YStack>
  );
};

const AlertsTable = ({
  alerts,
  sensors,
  customers,
  onViewAlertPress,
}: {
  alerts: SensorAlert[];
  sensors: Sensor[];
  customers: Customer[];
  onViewAlertPress: (sensorAlertId: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Table>
      <Table.Head>
        <Table.Row rowLocation="first">
          <Table.HeaderCell>{t("alerts.table.alert")}</Table.HeaderCell>
          <Table.HeaderCell>{t("alerts.table.sensor")}</Table.HeaderCell>
          <Table.HeaderCell>{t("alerts.table.customer")}</Table.HeaderCell>
          <Table.HeaderCell>{t("alerts.table.date")}</Table.HeaderCell>
          <Table.HeaderCell>{t("alerts.table.details")}</Table.HeaderCell>
          <Table.HeaderCell>{t("alerts.table.severity")}</Table.HeaderCell>
          <Table.HeaderCell>{t("alerts.table.action")}</Table.HeaderCell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {alerts.map((alert, index) => {
          const sensor = sensors.find((sensor) => sensor.id === alert.sensorId);
          const customer = customers.find(
            (customer) => customer.id === alert.customerId,
          );
          return (
            <AlertsTableRow
              key={index}
              alert={alert}
              sensor={sensor}
              customer={customer}
              onViewAlertPress={onViewAlertPress}
              isFirst={index === 0}
              isLast={index === alerts.length - 1}
            />
          );
        })}
      </Table.Body>
    </Table>
  );
};

const AlertsTableRow = ({
  alert,
  sensor,
  customer,
  onViewAlertPress,
  isFirst,
  isLast,
}: {
  alert: SensorAlert;
  sensor?: Sensor;
  customer?: Customer;
  onViewAlertPress: (sensorAlertId: string) => void;
  isFirst: boolean;
  isLast: boolean;
}) => {
  const { generateAlertStatusPillProps } = useStatusPillProps();
  const statusPillProps = generateAlertStatusPillProps(alert);
  const { t } = useTranslation();

  const typeKey = Object.entries(SensorAlertType).find(
    ([, value]) => value === alert.alertType,
  )?.[0];
  const typeLabel = typeKey
    ? t([
        `alerts.type.${typeKey[0].toLowerCase()}${typeKey.slice(1)}`,
        "alerts.type.unknown",
      ])
    : t("alerts.type.unknown");
  const date = new Date((alert.timestamp || 0) * 1000);

  // Format humidity alert messages with additional details
  const formatHumidityAlertMessage = (originalMessage: string): string => {
    if (alert.alertType === SensorAlertType.HighHumidity) {
      const humidity = alert.value || "N/A";
      const location = sensor?.location || "Unknown Location";

      // Format time as "3:33pm" style
      const time = date
        .toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
        .toLowerCase();

      // Format date as "10/09" style (MM/DD)
      const formattedDate = date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
      });

      // Map severity to descriptive text with humidity details
      switch (alert.severity) {
        case "medium":
          return `Slightly Humid (${humidity}%) in ${location} at ${time} on ${formattedDate}`;
        case "high":
          return `Humid (${humidity}%) in ${location} at ${time} on ${formattedDate}`;
        case "critical":
          return `Extremely Humid (${humidity}%) in ${location} at ${time} on ${formattedDate}`;
        default:
          return `Humid (${humidity}%) in ${location} at ${time} on ${formattedDate}`;
      }
    }
    return originalMessage || "";
  };

  return (
    <Table.Row
      key={alert.id}
      rowLocation={isFirst && !isLast ? "first" : isLast ? "last" : "middle"}
    >
      <Table.Cell>
        <Text fontWeight="bold">{typeLabel}</Text>
      </Table.Cell>
      <Table.Cell maxWidth="$12">
        <Text
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          width="100%"
        >
          {sensor?.name ?? ""}
        </Text>
      </Table.Cell>
      <Table.Cell gap="$1.5">
        {customer && (
          <>
            <Text>{customer.name}</Text>
            <Text fontSize="$3" color="$white10">
              {t("alerts.table.customerAddress", {
                street: customer.address,
                city: customer.city,
                state: customer.state,
                zip: customer.zipCode,
              })}
            </Text>
          </>
        )}
      </Table.Cell>
      <Table.Cell>{t("alerts.table.dateValue", { date })}</Table.Cell>
      <Table.Cell>{formatHumidityAlertMessage(alert.message || "")}</Table.Cell>
      <Table.Cell>
        <StatusPill {...statusPillProps} />
      </Table.Cell>
      <Table.Cell>
        <OutlinedButton
          rounded
          icon={Eye}
          fontSize="$3"
          onPress={() => onViewAlertPress(alert.id)}
        >
          {t("alerts.table.viewButton")}
        </OutlinedButton>
      </Table.Cell>
    </Table.Row>
  );
};

export default Alerts;
